import { useCallback, useState } from 'react'

import { DateTime } from 'luxon'

import { ChartNavControls } from '@/components/charts/history/ChartNavControls'
import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import type { InsightParametersType } from '@/types/dashboard-types'

/** The ←/→ buttons move the period by this much, whatever its length. */
const STEP_HOURS = 24

/** What an asset insight is asked for one asset over a From/To window. */
export interface AssetWindowParams extends InsightParametersType {
  companyId: number
  assetId: string
  date: 'custom'
  from: string
  to: string
}

/** Today's calendar day (00:00 → next 00:00) in `zone`, not a rolling last 24 hours. */
export const todayWindow = (zone: string): HistoryWindow => {
  const from = DateTime.now().setZone(zone).startOf('day')
  return { from, to: from.plus({ hours: 24 }) }
}

export const sameWindow = (a: HistoryWindow, b: HistoryWindow) => a.from.equals(b.from) && a.to.equals(b.to)

/**
 * The API reads a custom range as wall-clock times in the user's profile timezone, which is
 * the zone the period is kept in. `to` is exclusive here and inclusive there, a second's odds.
 */
export const assetWindowParams = (companyId: number, assetId: number, period: HistoryWindow): AssetWindowParams => {
  const wallClock = (dt: DateTime) => dt.toFormat("yyyy-MM-dd'T'HH:mm:ss")
  return {
    companyId,
    assetId: String(assetId),
    date: 'custom',
    from: wallClock(period.from),
    to: wallClock(period.to.minus({ seconds: 1 })),
  }
}

/** The period the asset detail tabs share, and today, which reset returns to. */
export interface AssetWindowState {
  initial: HistoryWindow
  period: HistoryWindow
  setPeriod: (period: HistoryWindow) => void
  zone: string
}

/**
 * One period for all of an asset's date-driven tabs, so moving it in one moves it in the
 * others. Opens on today; setting an equal window is a no-op, so echoes don't re-render.
 */
export const useAssetWindow = (zone: string): AssetWindowState => {
  const [initial] = useState(() => todayWindow(zone))
  const [period, setPeriodState] = useState<HistoryWindow>(initial)
  const setPeriod = useCallback(
    (next: HistoryWindow) => setPeriodState((prev) => (sameWindow(prev, next) ? prev : next)),
    []
  )
  return { initial, period, setPeriod, zone }
}

interface AssetWindowNavProps {
  assetWindow: AssetWindowState
  /** Controls at the left end, e.g. an insight picker; the date picker sits at the right. */
  leading?: React.ReactNode
}

/**
 * The asset detail tabs' date bar: step a day back or forward, pick any From/To range, or go
 * back to today.
 */
export const AssetWindowNav = ({ assetWindow, leading }: AssetWindowNavProps) => {
  const { initial, period, setPeriod, zone } = assetWindow
  const shift = (direction: -1 | 1) => {
    setPeriod({
      from: period.from.plus({ hours: direction * STEP_HOURS }),
      to: period.to.plus({ hours: direction * STEP_HOURS }),
    })
  }

  // Inset like the Temperature Graph's, so the date picker stays put when switching tab.
  return (
    <div className="px-4">
      <ChartNavControls
        periodLabel="24 Hours"
        periodWindow={[period.from.toMillis(), period.to.toMillis()]}
        // No range to stay inside: the buttons move the period itself.
        range={null}
        from={initial.from}
        zone={zone}
        isModified={!sameWindow(period, initial)}
        isZoomed={false}
        onResetZoom={() => undefined}
        zoomHint=""
        navigate={shift}
        jumpToDate={() => undefined}
        resetToDefault={() => setPeriod(initial)}
        showRange
        onRangeChange={(next) => setPeriod(next ?? initial)}
        leading={leading}
      />
    </div>
  )
}
